// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Brush Preset Library (Phase 3.4)
// ═══════════════════════════════════════════════════════════════
// Built-in presets + save/load from localStorage.
// Each preset captures all brush settings as a JSON object.
// ═══════════════════════════════════════════════════════════════

export const BUILT_IN_PRESETS = [
  { name: "Round Soft",    tip: "round",    size: 20, hardness: 0.3, opacity: 1, flow: 1, spacing: 10, sizeJitter: 0, angleJitter: 0, opacityJitter: 0, scatter: 0 },
  { name: "Round Hard",    tip: "round",    size: 12, hardness: 1,   opacity: 1, flow: 1, spacing: 8, sizeJitter: 0, angleJitter: 0, opacityJitter: 0, scatter: 0 },
  { name: "Pencil",        tip: "round",    size: 3,  hardness: 1,   opacity: 0.9, flow: 1, spacing: 5, sizeJitter: 0.05, angleJitter: 0, opacityJitter: 0.1, scatter: 0 },
  { name: "Marker",        tip: "square",   size: 16, hardness: 0.9, opacity: 0.85, flow: 0.8, spacing: 20, sizeJitter: 0, angleJitter: 0, opacityJitter: 0, scatter: 0 },
  { name: "Flat Brush",    tip: "flat",     size: 24, hardness: 0.7, opacity: 1, flow: 1, spacing: 8, sizeJitter: 0, angleJitter: 0, opacityJitter: 0, scatter: 0 },
  { name: "Calligraphy",   tip: "flat",     size: 18, hardness: 0.9, opacity: 1, flow: 1, spacing: 5, sizeJitter: 0, angleJitter: 0, opacityJitter: 0, scatter: 0 },
  { name: "Charcoal",      tip: "texture",  size: 30, hardness: 0.4, opacity: 0.7, flow: 0.6, spacing: 30, sizeJitter: 0.15, angleJitter: 0.3, opacityJitter: 0.2, scatter: 0.05 },
  { name: "Chalk",         tip: "texture",  size: 20, hardness: 0.5, opacity: 0.8, flow: 0.7, spacing: 25, sizeJitter: 0.1, angleJitter: 0.2, opacityJitter: 0.15, scatter: 0.03 },
  { name: "Watercolor",    tip: "round",    size: 40, hardness: 0.1, opacity: 0.3, flow: 0.2, spacing: 20, sizeJitter: 0.2, angleJitter: 0, opacityJitter: 0.3, scatter: 0.1 },
  { name: "Hair Brush",    tip: "hair",     size: 30, hardness: 0.5, opacity: 0.8, flow: 0.9, spacing: 35, sizeJitter: 0.1, angleJitter: 0.5, opacityJitter: 0.1, scatter: 0.05 },
  { name: "Grass",         tip: "grass",    size: 40, hardness: 0.6, opacity: 0.9, flow: 1, spacing: 40, sizeJitter: 0.2, angleJitter: 0.3, opacityJitter: 0.1, scatter: 0.15 },
  { name: "Ink Splatter",  tip: "splatter", size: 50, hardness: 0.8, opacity: 0.9, flow: 1, spacing: 80, sizeJitter: 0.4, angleJitter: 1, opacityJitter: 0.2, scatter: 0.3 },
  { name: "Spray Paint",   tip: "spray",    size: 60, hardness: 0.3, opacity: 0.5, flow: 0.3, spacing: 15, sizeJitter: 0.1, angleJitter: 0, opacityJitter: 0.2, scatter: 0.2 },
  { name: "Eraser Soft",   tip: "round",    size: 30, hardness: 0.2, opacity: 1, flow: 1, spacing: 20, sizeJitter: 0, angleJitter: 0, opacityJitter: 0, scatter: 0, _eraser: true },
  { name: "Eraser Hard",   tip: "round",    size: 20, hardness: 1,   opacity: 1, flow: 1, spacing: 15, sizeJitter: 0, angleJitter: 0, opacityJitter: 0, scatter: 0, _eraser: true },
];

const STORAGE_KEY = "pixalloy_brush_presets";

/**
 * Load user-saved presets from localStorage.
 * Returns an array of preset objects (may be empty).
 */
export function loadUserPresets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
}

/**
 * Save a preset to localStorage.
 * @param {object} preset — must have { name, tip, size, hardness, opacity, flow, spacing, ... }
 */
export function saveUserPreset(preset) {
  const existing = loadUserPresets();
  // Replace if same name exists
  const idx = existing.findIndex(p => p.name === preset.name);
  if (idx >= 0) existing[idx] = preset;
  else existing.push(preset);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(existing)); } catch {}
  return existing;
}

/**
 * Delete a user preset by name.
 */
export function deleteUserPreset(name) {
  const existing = loadUserPresets().filter(p => p.name !== name);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(existing)); } catch {}
  return existing;
}

/**
 * Export all presets (built-in + user) as a JSON string for download.
 */
export function exportPresets() {
  return JSON.stringify({ builtIn: BUILT_IN_PRESETS, user: loadUserPresets() }, null, 2);
}

/**
 * Apply a preset to the brush state setters.
 * @param {object} preset
 * @param {object} setters — { setBsz, setBhard, setBop, setBtip, setBrushFlow, setBrushSpacing, setSizeJitter, setAngleJitter, setOpacityJitter, setScatterAmount, setTool }
 */
export function applyPreset(preset, setters) {
  const { setBsz, setBhard, setBop, setBtip, setBrushFlow, setBrushSpacing,
    setSizeJitter, setAngleJitter, setOpacityJitter, setScatterAmount, setTool, T } = setters;
  if (preset.size != null) setBsz(preset.size);
  if (preset.hardness != null) setBhard(preset.hardness);
  if (preset.opacity != null) setBop(preset.opacity);
  if (preset.tip != null) setBtip(preset.tip);
  if (preset.flow != null) setBrushFlow(preset.flow);
  if (preset.spacing != null) setBrushSpacing(preset.spacing);
  if (preset.sizeJitter != null) setSizeJitter(preset.sizeJitter);
  if (preset.angleJitter != null) setAngleJitter(preset.angleJitter);
  if (preset.opacityJitter != null) setOpacityJitter(preset.opacityJitter);
  if (preset.scatter != null) setScatterAmount(preset.scatter);
  // Switch to eraser tool for eraser presets
  if (preset._eraser && setTool && T) setTool(T.ERASER);
  else if (!preset._eraser && setTool && T) setTool(T.BRUSH);
}
